import { formatDate } from '@keplerco/core';
import React, { useEffect, useState } from 'react';
import { EmptyState } from '../../../../../components/general/empty-state/empty-state';
import { AssessmentAssigneeType } from '../../../../../enums';
import { AssessmentSubType, AssessmentType } from '../../../../../enums/assessment-type';
import { AssessmentOverviewResponse, PeerOverviewResponse } from '../../../../../models/overmind/assessment-analytics';
import { AssessmentSearchParams, PermissionSearchParams } from '../../../../../models/overmind/search-params';
import { useAppActions } from '../../../../../overmind';
import { AssessmentConfigurationWidget } from '../assessment-configuration/assessment-configuration.widget';
import { AssessmentIncludedFOPWidget } from '../../../../../widgets/analytics/included-fop/assessment-included-fop.widget';
import { ParticipantsWidget } from '../../../../../widgets/analytics/participants/participants.widget';
import { AssessmentIncludedQuestionnairesWidget } from '../../../../../widgets/analytics/questionnaires/assessment-included-questionnaires.widget';
import { EntitiesAssignedWidget } from '../entities-assigned/entities-assigned.widget';
import SkillsAssignedWidget from '../../../skills-assigned/skills-assigned.widget';
import AssessmentCompletionWidget from '../../../assessment-completion/assessment-completion.widget';
import { AssessmentTypeWidget } from '../../assessment-type/assessment-type.widget';
import styles from './assessment-details.module.css';
import { SkeletonOverlay } from '../../../../../components/general/loading-state/skeleton-overlay';
import { AssessmentStatusChip } from '../../../../../components/chips/assessment-status.chip';
import { DEFAULT_FILTERING_SEARCH_PARAMS } from '../../../../../library/consts/consts';
import { AssessmentStatus } from '../../../../../enums/assessment-status';
import { AssessmentResponse } from '../../../../../overmind/actions';

export function AssessmentDetailsLayout({ assessment, baseSearchParams }: { assessment: AssessmentResponse; baseSearchParams: AssessmentSearchParams & PermissionSearchParams }): JSX.Element {
  const actions = useAppActions();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [overviewData, setOverviewData] = useState<AssessmentOverviewResponse>();
  const [peerOverviewData, peerOverviewOverviewData] = useState<PeerOverviewResponse>();

  useEffect(() => {
    async function getData() {
      if (!baseSearchParams) return;
      setIsLoading(true);
      const response = await actions.getAssessmentOverview({ ...baseSearchParams, ...DEFAULT_FILTERING_SEARCH_PARAMS });
      setOverviewData(response);

      if (baseSearchParams.assessmentSubType === AssessmentSubType.RatingAssessment) {
        const response = await actions.getAssessmentAnalyticsPeerOverview({ assessmentSlug: assessment.slug, companySlug: baseSearchParams.companySlug! });
        peerOverviewOverviewData(response);
      }
      setIsLoading(false);
    }

    getData();
  }, [baseSearchParams]);

  return (
    <SkeletonOverlay isLoading={isLoading} width="100%" minHeight="100vh">
      {!assessment || !overviewData ? (
        <EmptyState title="No available data" />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24, width: '100%' }}>
          <div className={styles.overview}>
            <h4 style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              Assessment overview
              <AssessmentStatusChip assessmentStatus={assessment.assessmentStatus} />
            </h4>

            <div style={{ display: 'flex', gap: 12 }}>
              <small>Activated: {formatDate(new Date(overviewData.activatedDate))}</small>
              {assessment.assessmentStatus === AssessmentStatus.Closed && <small>Closed: {formatDate(new Date(overviewData.closedDate))}</small>}
            </div>
          </div>

          <div className="row" style={{ width: '100%', alignItems: 'stretch', gap: 24 }}>
            <div className="col-fill col-mobile-12" style={{ display: 'flex' }}>
              <AssessmentTypeWidget isLoading={isLoading} assessmentSubType={assessment?.assessmentSubType} />
            </div>

            <div className="col-fill col-mobile-12" style={{ display: 'flex' }}>
              <SkillsAssignedWidget isLoading={isLoading} skillCount={overviewData.totalSkills} />
            </div>

            <div className="col-fill col-mobile-12" style={{ display: 'flex' }}>
              <AssessmentConfigurationWidget isLoading={isLoading} overviewData={overviewData} ratingOverview={peerOverviewData} />
            </div>

            <div className="col-fill col-mobile-12" style={{ display: 'flex' }}>
              <EntitiesAssignedWidget isLoading={isLoading} entityType={assessment.assigneeType} overviewData={overviewData} peerOverview={peerOverviewData} />
            </div>
          </div>

          <div className={styles.completionParticipants} style={{ gridTemplateColumns: (assessment.assigneeType === AssessmentAssigneeType.Department || assessment.assigneeType === AssessmentAssigneeType.Team) && overviewData.assigneeTypeCount > 1 ? '1fr 1fr' : '1fr' }}>
            <AssessmentCompletionWidget baseSearchParams={baseSearchParams} />

            {(assessment.assigneeType === AssessmentAssigneeType.Department || assessment.assigneeType === AssessmentAssigneeType.Team) && overviewData.assigneeTypeCount > 1 && <ParticipantsWidget baseSearchParams={baseSearchParams} />}
          </div>

          {assessment.assessmentType === AssessmentType.Questionnaire && <AssessmentIncludedQuestionnairesWidget assessmentSlug={assessment.slug} />}

          {assessment.assessmentType === AssessmentType.FieldsOfPractice && <AssessmentIncludedFOPWidget assessmentSlug={assessment.slug} />}
        </div>
      )}
    </SkeletonOverlay>
  );
}
